import * as React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
import BaseTimePicker from '../inputs/timePickers/BaseTimePicker';
import CustomTimeInput from 'shared/inputs/timePickers/CustomTimeInput';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A time picker form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<BaseTimePicker>} BaseTimePickerProps
 * @typedef {React.ComponentPropsWithoutRef<CustomTimeInput>} CustomTimeInputProps
 *
 * @param {BaseFieldRenderProps & BaseTimePickerProps & CustomTimeInputProps} fieldRenderProps
 * @returns {React.ReactElement} Time picker form field.
 */
const FormTimePicker = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    disabled,
    hint,
    wrapperStyle,
    optional,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';

  if (typeof others.value === 'string' && others.value.length > 0) {
    others.value = new Date(`1970-01-01 ${ others.value.match(/\d\d:\d\d/gi)[0] }:00`);
  }
  const handleOnChange = (e) => {
    const mimicEvent = { target: { value: e.value } };
    others.onChange(mimicEvent);
  };

  if (others.minimal) {
    return (
      <CustomTimeInput
        validationMessage={validationMessage}
        touched={touched}
        label={label}
        id={editorId}
        name={name}
        valid={valid}
        disabled={disabled}
        hint={hint}
        wrapperStyle={wrapperStyle}
        value={others.value}
        {...others}
        style={{ backgroundColor: '', width: '8rem', zoom: '.8' }}
        onChange={handleOnChange}
      />
    );
  }

  const { value } = others;

  if (!label) {
    return (
      <FieldWrapper style={wrapperStyle}>
        <BaseTimePicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          {...others}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper style={wrapperStyle}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={(!value || (typeof value === 'string' && value.length === 0)) ? null : value}
      >
        <BaseTimePicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          {...others}
        />
      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormTimePicker;
